import {useState, useEffect, useRef} from 'react';

/**
 * Simple countdown hook that counts down from expiry timestamp
 */
export const useExpiryCountdown = (
  expiryTimestamp: number | null,
  onExpired: () => void,
) => {
  const [timeLeft, setTimeLeft] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const onExpiredRef = useRef(onExpired);

  useEffect(() => {
    onExpiredRef.current = onExpired;
  }, [onExpired]);

  useEffect(() => {
    if (!expiryTimestamp) {
      setTimeLeft(0);
      return;
    }

    const updateTimeLeft = () => {
      const now = Math.floor(Date.now() / 1000);
      const remaining = Math.max(0, expiryTimestamp - now);
      setTimeLeft(remaining * 1000); // Convert to milliseconds for display

      if (remaining <= 0) {
        onExpiredRef.current();
      }
    };

    updateTimeLeft();

    // Clear any existing interval before setting a new one
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(updateTimeLeft, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [expiryTimestamp]);

  const formatTime = (timeMs: number): string => {
    const minutes = Math.floor(timeMs / 60000);
    const seconds = Math.floor((timeMs % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return {
    timeLeft,
    formatTime: () => formatTime(timeLeft),
  };
};
